import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack, Tooltip } from '@mui/material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import {
    SCUICustomDataGrid,
    SCUITextFieldWithButton,
    SCUIBreadCrumb,
    SCUIBatchSearchModule,
} from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { SCUINoRecordComponent } from '../../atomic/atoms';
import { PostCheckoutCommand } from '../../services/command';
import Swal from 'sweetalert2';
import { PageUrl } from '@/types/enums/pageUrl-enums';
import { TCD } from './transferCargoData.classes';

export const TransferCargoData: FC = () => {
    const [orderNo, setOrderNo] = useState('');
    const [rows, setRows] = useState<TransferCargoRow[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const { t } = useTranslation();

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            { field: 'id', headerName: 'Id', flex: 1, hide: true },
            {
                field: 'shippingNo',
                headerName: t('general.shippingNo'),
                flex: 1,
                renderCell: (params) => (
                    <Tooltip title={String(params.value)}>
                        <span className={TCD.cellShippingWrap}>{String(params.value)}</span>
                    </Tooltip>
                ),
            },
            {
                field: 'message',
                headerName: 'Message',
                flex: 1,
                renderCell: (params) => (
                    <Tooltip title={params.row.message || ''}>
                        <span className={TCD.cellMessageWrap}>{params.row.message}</span>
                    </Tooltip>
                ),
            },
        ],
        [t]
    );

    const queryTransferData = useCallback(async () => {
        const shippingListNo = orderNo.trim().split('\n').join(',');

        if (!orderNo || orderNo.trim().length === 0) {
            Swal.fire(t('mobileApp.noEmpty'), ' ', 'error');
            return;
        }

        setIsSearching(true);
        try {
            const response = await PostCheckoutCommand.ReSendLabelMessage({ shippingListNo });
            setRows([
                {
                    id: shippingListNo,
                    shippingNo: shippingListNo,
                    message: response.message || '',
                },
            ]);
        } catch {
            setRows([
                {
                    id: shippingListNo,
                    shippingNo: shippingListNo,
                    message: '',
                },
            ]);
        } finally {
            setIsSearching(false);
        }
    }, [orderNo, t]);

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setOrderNo(event.target.value);
    }, []);

    const handleEnterKey = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                queryTransferData();
            }
        },
        [queryTransferData]
    );

    return (
        <SCUIContent className={TCD.page}>
            <div className={TCD.breadcrumbSection}>
                <SCUIBreadCrumb title={t('setupLabelPrinting.name')} componentLink={PageUrl.transferCargoData.url} />
            </div>
            {isSearching && (
                <div className={TCD.loadingWrap}>
                    <CircularProgress size={24} sx={{ mb: 2 }} />
                </div>
            )}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2, md: 4, xl: 20 }}
                className={TCD.actionsRow}
            >
                <Box sx={{ '& .MuiButton-root': { padding: '4px 6px' } }} className={TCD.singleSearchWrap}>
                    <SCUITextFieldWithButton
                        placeholder={t('general.shippingNo')}
                        buttonText={t('general.sendInfo')}
                        onChange={handleInputChange}
                        onClick={queryTransferData}
                        onKeyPress={handleEnterKey}
                        paperClassName={TCD.singleSearchPaper}
                        inputClassName={TCD.singleSearchInput}
                        buttonClassName={TCD.singleSearchButton}
                    />
                </Box>
                <div className={TCD.batchModuleWrap}>
                    <SCUIBatchSearchModule
                        function={queryTransferData}
                        value={orderNo}
                        setValue={setOrderNo}
                        textFieldLabel={t('general.shippingNo')}
                        isSearching={isSearching}
                    />
                </div>
            </Stack>
            {rows?.length > 0 ? (
                <div className={TCD.dataGridWrap}>
                    <SCUICustomDataGrid
                        rows={rows}
                        columns={columns}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    {
                                        field: 'orderNumber',
                                        sort: 'asc',
                                    },
                                ],
                            },
                        }}
                    />
                </div>
            ) : (
                <div className={TCD.noRecordWrap}>
                    <SCUINoRecordComponent />
                </div>
            )}
        </SCUIContent>
    );
};

interface TransferCargoRow {
    id: string;
    shippingNo: string;
    message: string;
}
